import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { PropsOf } from '@emotion/react';
import { css } from '@emotion/react';
import { tokens } from '@hotjar/hotsauce';

export interface TableProps extends PropsOf<'table'> {
  /**
   * Styles override.
   */
  className?: string;

  /**
   * Accessible headline for the table.
   * Only visible to screen readers.
   */
  caption?: string;

  /**
   * A name for the table. It's used for accessibility.
   */
  tableName?: string;

  /**
   * Children of the table.
   */
  children?: ReactNode;
}

/**
 * @private
 */
type StyledTableProps = Omit<TableProps, 'caption' | 'tableName'>;

/**
 * Custom Styled table component.
 * We needed a table with different requirements
 * than the one from the DS (REC-350)
 *
 * @private
 */
const SECOND_COLUMN_OFFSET = 50;
const SECOND_COLUMN_WIDTH = 131;
const StyledTable = styled('table')<StyledTableProps>`
  display: table;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  position: relative;

  & svg {
    position: static;
  }

  & thead th:first-of-type,
  & thead th:nth-of-type(2) {
    position: sticky;
    left: 0;
    z-index: 2;
  }

  & thead th:nth-of-type(2) {
    left: ${SECOND_COLUMN_OFFSET}px;
    border-right: 1px solid ${tokens.color.ui_border_light};
  }

  & tbody tr td:first-of-type,
  & tbody tr td:nth-of-type(2) {
    position: sticky;
    left: 0;
    z-index: 0;
  }

  & tbody tr td:nth-of-type(2) {
    min-width: ${SECOND_COLUMN_WIDTH}px;
    width: ${SECOND_COLUMN_WIDTH}px;
    left: ${SECOND_COLUMN_OFFSET}px;
    border-right: 1px solid ${tokens.color.ui_border_light};
  }

  & input[type='checkbox'] + span {
    margin: 0;
  }

  & + .endOfTableShadow {
    transition: width 125ms linear;

    height: 100%;
    width: 0;

    background: linear-gradient(
      270deg,
      ${tokens.BRAND_COLORS.neutral_100} 0%,
      rgba(255, 255, 255, 0) 100%
    );

    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;

    &.visible {
      width: 30px;
    }
  }
`;

/**
 * Caption for the table, used for accessibility purposed.
 * @private
 */
const AccessibilityCaption = styled('caption')`
  display: none;
`;

const Container = styled.div`
  overflow: auto;
`;

const ShadowContainer = styled.div<{ isVisible: boolean }>`
  position: relative;

  ${({ isVisible }) =>
    isVisible &&
    css`
      &:after {
        content: '';
        height: 100%;
        width: 30px;
        background: linear-gradient(
          90deg,
          ${tokens.BRAND_COLORS.neutral_100} 0%,
          rgba(255, 255, 255, 0) 100%
        );
        position: absolute;
        left: ${SECOND_COLUMN_WIDTH + SECOND_COLUMN_OFFSET}px;
        top: 0;
        z-index: 1;
      }
    `}
`;

const ScrollContainer = styled.div<{ isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  overflow-x: auto;
  overflow-y: hidden;
  position: fixed;
  bottom: 0;
  height: 30px;
`;

const Scrollbar = styled.div`
  height: 30px;
`;

/**
 * Recordings Table custom component.
 * We had to diverge from the specs of the DS Table component due to
 * different requirements (Recordings table has left sticky columns)
 * See REC-350
 */
export const RecordingsTable = ({
  caption,
  tableName,
  children,
  ...props
}: TableProps): JSX.Element => {
  const tableRef = useRef<HTMLTableElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const endOfContainerRef = useRef<HTMLDivElement>(null);
  const tableRightLimitRef = useRef<HTMLDivElement>(null);
  const [isLeftShadowVisible, setLeftShadowVisible] = useState<boolean>(false);
  const [isExtraScrollVisible, setIsExtraScrollVisible] = useState<boolean>(false);

  // init the table headings with the className 'isSticky' so that the StickyHeadRow works
  useEffect(() => {
    if (tableRef.current && containerRef.current) {
      const tableElement = tableRef.current;
      const firstColumn = tableElement.querySelectorAll('thead tr>th:first-child');
      const secondColumn = tableElement.querySelectorAll('thead tr>th:nth-child(2)');
      firstColumn[0].classList.add('isSticky');
      firstColumn[1].classList.add('isSticky');
      secondColumn[0].classList.add('isSticky');
      secondColumn[1].classList.add('isSticky');
    }
  }, [tableRef, containerRef]);

  // observes the horizontal scroll to know when the shadow on the third column should show up
  useEffect(() => {
    if (tableRef.current && containerRef.current) {
      const tableElement = tableRef.current;

      const thirdColumn = tableElement.querySelector('thead tr>th:nth-child(3)');
      let observer: IntersectionObserver;

      if (thirdColumn) {
        observer = new IntersectionObserver(
          ([e]) => {
            setLeftShadowVisible(e.intersectionRatio !== 1);
          },
          { threshold: [1], root: containerRef.current, rootMargin: '0px 0px 0px -181px' },
        );

        observer.observe(thirdColumn);
      }

      // unmount
      return function () {
        observer && thirdColumn && observer.unobserve(thirdColumn);
      };
    }
  }, [tableRef, containerRef, children]);

  // observes whether the last column is fully visible, to add/remove the shadow on the right of the table
  useEffect(() => {
    if (tableRef.current && containerRef.current && tableRightLimitRef.current) {
      const tableElement = tableRef.current;
      const tableRightLimit = tableRightLimitRef.current;
      const lastColumn = tableElement.querySelectorAll('thead tr>th:last-child')[0];
      const observer = new IntersectionObserver(
        ([e]) => {
          const isLastColumnVisible = e.intersectionRatio === 1;

          if (isLastColumnVisible) {
            tableRightLimit.classList.remove('visible');
          } else {
            tableRightLimit.classList.add('visible');
          }
        },
        { threshold: [1], root: containerRef.current },
      );

      observer.observe(lastColumn);

      // unmount
      return function () {
        observer.unobserve(tableElement);
      };
    }
  }, [tableRef, containerRef]);

  useEffect(() => {
    if (!endOfContainerRef.current) {
      return;
    }

    // we're keeping track of a dummy div at the end of the
    // container so we can easily know when the default scrollbar
    // is visible or not.
    const endOfTableContainer = endOfContainerRef.current;

    // check when the extra scrollbar is visible
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsExtraScrollVisible(e.intersectionRatio < 1);
      },
      { threshold: [1] },
    );

    // attach observers
    observer.observe(endOfTableContainer);

    // unmount
    return function () {
      observer.unobserve(endOfTableContainer);
    };
  }, [endOfContainerRef]);

  // Extra scrollbar logic
  useEffect(() => {
    if (
      !tableRef.current ||
      !scrollContainerRef.current ||
      !scrollbarRef.current ||
      !containerRef.current
    ) {
      return () => null;
    }

    const scrollContainerElement = scrollContainerRef.current;
    const containerElement = containerRef.current;

    let cancelScrollOnContainerCounter = 0;
    let cancelScrollOnScrollContainerCounter = 0;

    // sync the size of both of the scrollbars
    const syncScrollBarsSizes = () => {
      if (
        !tableRef.current ||
        !scrollbarRef.current ||
        !containerRef.current ||
        !scrollContainerRef.current
      ) {
        return;
      }

      const currentTableMeasures = tableRef.current.getBoundingClientRect();
      scrollbarRef.current.style.width = `${currentTableMeasures.width}px`;

      const currentContainerMeasures = containerRef.current.getBoundingClientRect();
      scrollContainerRef.current.style.width = `${currentContainerMeasures.width}px`;
    };

    syncScrollBarsSizes();

    // Set correct initial values
    scrollContainerElement.scrollLeft = containerElement.scrollLeft;

    // sync the scroll of the extra scrollbar with the scroll of the table
    const containerListener = () => {
      if (cancelScrollOnContainerCounter !== 0) {
        cancelScrollOnContainerCounter -= 1;
        return;
      }

      cancelScrollOnScrollContainerCounter += 1;
      scrollContainerElement.scrollLeft = containerElement.scrollLeft;
    };

    // sync the scroll of the table with the extra scrollbar
    const scrollContainerListener = () => {
      if (cancelScrollOnScrollContainerCounter !== 0) {
        cancelScrollOnScrollContainerCounter -= 1;
        return;
      }

      cancelScrollOnContainerCounter += 1;
      containerElement.scrollLeft = scrollContainerElement.scrollLeft;
    };

    // Attach scroll behaviour to both of the scrollbars
    if (isExtraScrollVisible) {
      containerRef.current.addEventListener('scroll', containerListener, { passive: true });
      scrollContainerRef.current.addEventListener('scroll', scrollContainerListener, {
        passive: true,
      });
    }

    // Sync sizes on window resize
    const resizeObserver = new ResizeObserver(() => {
      syncScrollBarsSizes();
    });
    resizeObserver.observe(containerRef.current);

    // Sync sizes when the table size changes (added/removed columns)
    const tableResizeObserver = new ResizeObserver(() => {
      syncScrollBarsSizes();
    });
    tableResizeObserver.observe(tableRef.current);

    return () => {
      scrollContainerElement.removeEventListener('scroll', scrollContainerListener);
      containerElement.removeEventListener('scroll', containerListener);
      resizeObserver.disconnect();
      tableResizeObserver.disconnect();
    };
  }, [tableRef, scrollContainerRef, scrollbarRef, containerRef, isExtraScrollVisible]);
  return (
    <ShadowContainer isVisible={isLeftShadowVisible}>
      <Container ref={containerRef} data-id="dsTableContainer">
        <StyledTable
          {...props}
          role="table"
          aria-describedby={`table-caption-${tableName}`}
          ref={tableRef}
        >
          <AccessibilityCaption id={`table-caption-${tableName}`}>{caption}</AccessibilityCaption>
          {children}
        </StyledTable>
        <div className="endOfTableShadow visible" ref={tableRightLimitRef} />
        <div ref={endOfContainerRef} />
        <ScrollContainer ref={scrollContainerRef} isVisible={isExtraScrollVisible}>
          <Scrollbar ref={scrollbarRef} />
        </ScrollContainer>
      </Container>
    </ShadowContainer>
  );
};
